import { useState } from "react";
import { toast } from "sonner";
import { Hash, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { label } from "@/lib/format";

const yes = (v) => (v ? "Yes" : "No");

function EditDialog({ title, value, fields, url, onClose, onSaved, testid }) {
  const [f, setF] = useState(value);
  const save = async () => {
    try { value.id ? await api.put(`${url}/${value.id}`, f) : await api.post(url, f); toast.success("Saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {fields.map(([k, l, type, opts, hint]) => (
            <div key={k} className={type === "wide" ? "col-span-2" : ""}><Label>{l}</Label>
              {type === "bool" ? <div className="h-9 flex items-center"><Switch checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: v })} data-testid={`${testid}-${k}`} /></div>
                : type === "select" ? <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}><SelectTrigger data-testid={`${testid}-${k}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o.includes(" ") || /^[A-Z]/.test(o) ? o : label(o)}</SelectItem>)}</SelectContent></Select>
                : <Input value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${testid}-${k}`} />}
              {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
            </div>))}
        </div>
        <DialogFooter><Button onClick={save} data-testid={`${testid}-save`}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ASSET_OPTS = ["Not Required", "Required", "Optional"];
const LM_FIELDS = [["prefix", "Prefix *"], ["description", "Description *"], ["format", "Number format", "text", null, "{prefix}{seq} gives LMR6. {seq:4} pads to 0006."],
  ["projection_by_default", "Projection by default", "bool"], ["needs_approval", "Needs approval", "bool", null, "Off = auto-approved on submit"], ["active", "Active", "bool"],
  ["asset_link_requirement", "Asset link (Asset Group)", "select", ASSET_OPTS, "Required = needed before submit for approval and before dispatch"],
  ["asset_creation_after_dispatch", "Assets after dispatch", "select", ASSET_OPTS, "Required = delivery can't be confirmed until assets are created or skipped with a reason"],
  ["amc_asset_link_requirement", "AMC / CAMC contracts: asset link", "select", ASSET_OPTS, "Used instead of the above for AMC / CAMC LMOs"],
  ["amc_asset_creation_after_dispatch", "AMC / CAMC contracts: assets after dispatch", "select", ASSET_OPTS]];

export function LmTypes() {
  const { can } = useMe();
  const { data, reload } = useFetch("/lm-types");
  const [edit, setEdit] = useState(null);
  const admin = can("admin");
  const setNext = async () => {
    const n = window.prompt(`Next LM number (shared by all LM types). Currently ${data?.[0]?.next_number ?? 1}.`);
    if (!n) return;
    const reason = window.prompt("Reason for changing the numbering?");
    if (!reason) return;
    try { await api.post("/lm-types/next-number", { next_number: Number(n), reason }); toast.success("Next number set"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [
    { key: "prefix", label: "Prefix", mono: true }, { key: "description", label: "Description", wrap: true },
    { key: "next_number", label: "Next number", right: true }, { key: "format", label: "Format", mono: true }, { key: "preview", label: "Next LM no.", mono: true },
    { key: "projection_by_default", label: "Projection by default", exp: (r) => yes(r.projection_by_default), render: (r) => (r.projection_by_default ? <StatusBadge status="projection" /> : "No") },
    { key: "needs_approval", label: "Needs approval", exp: (r) => yes(r.needs_approval), render: (r) => yes(r.needs_approval) },
    { key: "asset_link_requirement", label: "Asset link" }, { key: "asset_creation_after_dispatch", label: "Assets after dispatch" },
    { key: "amc", label: "AMC / CAMC (link · after)", value: (r) => (r.prefix === "LMO" ? `${r.amc_asset_link_requirement} · ${r.amc_asset_creation_after_dispatch}` : ""), render: (r) => (r.prefix === "LMO" ? `${r.amc_asset_link_requirement} · ${r.amc_asset_creation_after_dispatch}` : "—") },
    { key: "active", label: "Active", exp: (r) => yes(r.active), render: (r) => <StatusBadge status={r.active ? "active" : "inactive"} /> },
    { key: "edit", label: "", exp: false, render: (r) => admin && <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEdit(r); }} data-testid={`lm-type-edit-${r.prefix}`}><Pencil className="h-4 w-4" /></Button> },
  ];
  return (
    <>
      <p className="text-xs text-slate-500 mb-2">LM numbers run on one shared sequence across all types (e.g. LMR6, LMS7, LMR8). Only admins can change LM types.</p>
      <DataTable testid="lm-types-table" columns={cols} rows={data} rowId={(r) => r.prefix} exportName="LM_Types"
        toolbar={admin && <><Button size="sm" variant="outline" onClick={setNext} data-testid="lm-next-number-btn"><Hash className="h-4 w-4 mr-1" />Set next number</Button>
          <Button size="sm" onClick={() => setEdit({ prefix: "", description: "", format: "{prefix}{seq}", projection_by_default: false, needs_approval: true, active: true, asset_link_requirement: "Not Required", asset_creation_after_dispatch: "Not Required", amc_asset_link_requirement: "Not Required", amc_asset_creation_after_dispatch: "Not Required" })} data-testid="lm-type-new-btn"><Plus className="h-4 w-4 mr-1" />New LM type</Button></>} />
      {edit && <EditDialog title={edit.id ? `Edit ${edit.prefix}` : "New LM type"} value={edit} fields={LM_FIELDS} url="/lm-types" testid="lm-type-dialog"
        onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </>
  );
}

const MT_FIELDS = [["code", "Code *"], ["name", "Name *"], ["direction", "Direction", "select", ["outward", "inward"]],
  ["party_type", "Party", "select", ["customer", "other", "none"], "Customer = picked from Customers Master"],
  ["links_lm", "Dispatch against LM lines", "bool", null, "Outward only: reduces LM pending qty, needs invoice/DC, LR & POD"], ["active", "Active", "bool"]];

export function MovementTypes() {
  const { can } = useMe();
  const { data, reload } = useFetch("/movement-types");
  const [edit, setEdit] = useState(null);
  const cols = [
    { key: "code", label: "Code", mono: true }, { key: "name", label: "Name", wrap: true },
    { key: "direction", label: "Direction", render: (r) => <StatusBadge status={r.direction} /> },
    { key: "links_lm", label: "Dispatch against LM", exp: (r) => yes(r.links_lm), render: (r) => yes(r.links_lm) },
    { key: "party_type", label: "Party", render: (r) => label(r.party_type) },
    { key: "active", label: "Active", exp: (r) => yes(r.active), render: (r) => <StatusBadge status={r.active ? "active" : "inactive"} /> },
    { key: "edit", label: "", exp: false, render: (r) => can("admin") && <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`mt-edit-${r.code}`}><Pencil className="h-4 w-4" /></Button> },
  ];
  return (
    <>
      <DataTable testid="movement-types-table" columns={cols} rows={data} exportName="Movement_Types"
        toolbar={can("admin") && <Button size="sm" onClick={() => setEdit({ code: "", name: "", direction: "inward", party_type: "customer", links_lm: false, active: true })} data-testid="mt-new-btn"><Plus className="h-4 w-4 mr-1" />New movement type</Button>} />
      {edit && <EditDialog title={edit.id ? `Edit ${edit.code}` : "New movement type"} value={edit} fields={MT_FIELDS} url="/movement-types" testid="mt-dialog"
        onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </>
  );
}
